import React, { useRef, useEffect, useState, useLayoutEffect } from "react";

export default function FullBodyPainter({ imgSrc, bodyParts, points=[], onPointCreated, onDeleteDraft, onOpenDiagnostico, showDebug = false, visible = true }) {
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const syncedRef = useRef(false);

  const [selectedColor, setSelectedColor] = useState("#FF00FF");
  const [sizePx, setSizePx] = useState(12);
  const [toolsOpen, setToolsOpen] = useState(false);

  const [ctxMenu, setCtxMenu] = useState({
    open: false,
    x: 0,
    y: 0,
    point: null, // { id, part, xPct, yPct, sizePct, color, diagnostico
  });

  // paleta
  const colors = ["#FF0000","#00FF00","#0000FF","#FFFF00","#FF00FF","#00FFFF"];

  // dibuja los puntos ya guardados
  const draw = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const rect = canvas.getBoundingClientRect();
    const cssWidth  = rect.width;
    const cssHeight = rect.height;

    if (!cssWidth || !cssHeight) {
      syncedRef.current = false;
      if(!rafRef.current) {
        rafRef.current = requestAnimationFrame(() => {
          rafRef.current = null;
          syncCanvasToImage();
        });
      }
      return;
    }

    syncedRef.current = true;

    ctx.clearRect(0, 0, cssWidth, cssHeight);

    const base = Math.min(cssWidth, cssHeight);

    points.forEach((p) => {
      const x = p.xPct * cssWidth;
      const y = p.yPct * cssHeight;
      const r = (p.sizePct ?? 0.02) * base;

      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fillStyle = (p.color || "#FF0000") + "80";
      ctx.fill();
    });
  };

  const syncCanvasToImage = () => {
    const img = imgRef.current;
    const canvas = canvasRef.current;
    if (!img || !canvas) return;

    const rect = img.getBoundingClientRect();
    if (!rect.width || !rect.height) {
      syncedRef.current = false;
      if(!rafRef.current) {
        rafRef.current = requestAnimationFrame(syncCanvasToImage);
      }
      return;
    }

    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.max(1, Math.round(rect.width * dpr));
    canvas.height = Math.max(1, Math.round(rect.height * dpr));

    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;

    const ctx = canvas.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    syncedRef.current = true;
    requestAnimationFrame(draw);
  };

  useEffect(() => {
    if(syncedRef.current) {
      requestAnimationFrame(draw);
    } else {
      requestAnimationFrame(syncCanvasToImage);
    }
  }, [points]);
  
  useEffect(() => {
    if(!visible) return;
    syncedRef.current = false;
    const id1 = requestAnimationFrame(() => {
      const id2 = requestAnimationFrame(() => {
        syncCanvasToImage(); // rect ya tiene el tamaño correcto
      });
      // guarda para limpiar ambos
      rafRef.current = id2;
    });
    return () => {
      cancelAnimationFrame(id1);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [visible]);
  
  useLayoutEffect(() => {
    if(rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    syncedRef.current = false;

    syncCanvasToImage();
    return () => {
      if(rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, [imgSrc])

  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;

    const ro = new ResizeObserver(() => {
      syncedRef.current = false;
      requestAnimationFrame(syncCanvasToImage)
    });
    ro.observe(img);

    const onResize = () => {
      syncedRef.current = false;
      syncCanvasToImage();
    };

    window.addEventListener("resize", onResize);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", onResize);
    };
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      syncedRef.current = false;
      // doble rAF para asegurar layout estable
      const id1 = requestAnimationFrame(() => {
        requestAnimationFrame(syncCanvasToImage);
      });
      return () => cancelAnimationFrame(id1);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ---------- helpers menú ----------
  const openCtxMenuForPoint = (point, clickX, clickY, rect) => {
    const PAD = 8;
    const menuW = 180; // aprox
    const menuH = 90;  // aprox
    const posX = Math.min(Math.max(0, clickX + 6), rect.width - menuW - PAD);
    const posY = Math.min(Math.max(0, clickY + 6), rect.height - menuH - PAD);
    setCtxMenu({ open: true, x: posX, y: posY, point });
  };

  const cancelPendingDraft = () => {
    // si hay borrador asociado al menú, lo borro
    if (ctxMenu.open && ctxMenu.point) {
      onDeleteDraft?.(ctxMenu.point);
    }
    setCtxMenu({ open: false, x: 0, y: 0, point: null });
  };

  // cerrar + borrar si clic fuera de todo el componente
  useEffect(() => {
    if (!ctxMenu.open) return;
    const handler = (ev) => {
      const root = containerRef.current;
      if (root && !root.contains(ev.target)) {
        cancelPendingDraft();
      }
    };
    window.addEventListener("mousedown", handler, true);
    return () => window.removeEventListener("mousedown", handler, true);
  }, [ctxMenu.open]);

  // cerrar + borrar con ESC
  useEffect(() => {
    if (!ctxMenu.open) return;
    const onKey = (e) => {
      if (e.key === "Escape") cancelPendingDraft();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [ctxMenu.open]);

  const handleClick = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    if (ctxMenu.open) {
      cancelPendingDraft();
      return;
    }

    const xPct = rect.width ? x / rect.width : 0;
    const yPct = rect.height ? y / rect.height : 0;

    const base = Math.min(rect.width, rect.height) || 1;

    for (let i = points.length - 1; i >= 0; i--) {
      const p = points[i];
      const px = (p.xPct ?? 0) * rect.width;
      const py = (p.yPct ?? 0) * rect.height;
      const r = ((p.sizePct ?? 0.02) * base) || 1;

      const dx = x - px;
      const dy = y - py;
      if (dx * dx + dy * dy <= r * r) {
        // Clic en punto existente -> abrir modal de edición
        // Pasamos el punto completo (incluye id/diagnostico si existen)
        onOpenDiagnostico?.(p);
        return;
      }
    }

    const partHit = Object.keys(bodyParts).find((key) => {
      const def = bodyParts[key];
      const L = parseFloat(def.left) / 100,
        T = parseFloat(def.top) / 100;
      const W = parseFloat(def.width) / 100,
        H = parseFloat(def.height) / 100;
      return xPct >= L && xPct <= L + W && yPct >= T && yPct <= T + H;
    });

    if (!partHit) return;

    const sizePct = Math.max(1, sizePx) / base;

    const id = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const point = { id, part: partHit, xPct, yPct, sizePct, color: selectedColor };

    onPointCreated?.(point);

    const PAD = 8;
    const menuW = 180; // aprox
    const menuH = 90;  // aprox
    const posX = Math.min(Math.max(0, x + 6), rect.width - menuW - PAD);
    const posY = Math.min(Math.max(0, y + 6), rect.height - menuH - PAD);
    // setCtxMenu({ open: true, x: posX, y: posY, point });
    openCtxMenuForPoint(point, x, y, rect);
  };

  const chooseDiagnostico = () => {
    if (!ctxMenu.point) return;
    onOpenDiagnostico?.(ctxMenu.point);
    setCtxMenu({ open: false, x: 0, y: 0, point: null });
  };

  const deleteDraft = () => {
    if (!ctxMenu.point) return;
    onDeleteDraft?.(ctxMenu.point);
    setCtxMenu({ open: false, x: 0, y: 0, point: null });
  };

  return (
    <div ref={containerRef}>
      <div className="sm:hidden flex justify-start mb-2">
        <button
          className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-white/90 border shadow text-sm"
          onClick={() => setToolsOpen((v) => !v)}
        >
          <span className="block w-4">
            <span className="block h-0.5 w-4 bg-black mb-1" />
            <span className="block h-0.5 w-4 bg-black mb-1" />
            <span className="block h-0.5 w-4 bg-black" />
          </span>
          {toolsOpen ? "Ocultar" : "Herramientas"}
        </button>
      </div>
      <div className="flex items-start gap-3">
        <aside
          className="
            hidden sm:flex
            flex-col gap-3 p-2 rounded-lg bg-white/80 backdrop-blur shadow
            w-[64px] max-h-[70vh] overflow-hidden
          "
        >
          {/* Slider vertical (rotado) */}
          <div className="flex flex-col items-center">
            <span className="text-[10px] mb-2">Tamaño</span>
            <div className="h-40 flex items-center justify-center">
              <input
                type="range"
                min="6"
                max="40"
                value={sizePx}
                onChange={(e) => setSizePx(parseInt(e.target.value))}
                className="origin-center rotate-[-90deg]"
                style={{ width: "160px" }}
                aria-label="Tamaño del punto"
              />
            </div>
            <span className="text-[11px]">{sizePx}px</span>
          </div>

          <div className="h-px bg-gray-300" />

          {/* Paleta vertical con scroll */}
          <div className="flex-1 overflow-y-auto pr-1" style={{ scrollbarWidth: "thin" }}>
            <div className="grid grid-cols-1 gap-2">
              {colors.map((c) => (
                <button
                  key={c}
                  onClick={() => setSelectedColor(c)}
                  title={c}
                  className="mx-auto"
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    background: c,
                    border: selectedColor === c ? "2px solid #000" : "1px solid #bbb",
                  }}
                />
              ))}
            </div>
          </div>
        </aside>

        <div className="relative inline-block">
          <img 
          ref={imgRef} 
          src={imgSrc}
          alt="cuerpo" 
          className="object-cover border border-dashed border-subMain w-full h-auto"
          onLoad={syncCanvasToImage} />

          {/* canvas absoluto encima */}
          <canvas ref={canvasRef}
                  onClick={handleClick}
                  style={{ position:"absolute", inset:0, pointerEvents:"auto" }}
                  className="z-30"/>
          
          {showDebug && (
              <div className="absolute inset-0 z-20 pointer-events-none">
                {Object.entries(bodyParts).map(([key, def]) => (
                <div
                  key={key}
                  style={{
                    position: "absolute",
                    left: def.left,
                    top: def.top,
                    width: def.width,
                    height: def.height,
                    border: "1px dashed red",
                    background: "rgba(255,0,0,0.04)",
                  }}
                >
                  <span
                    style={{
                      position: "absolute",
                      left: 2,
                      top: 2,
                      fontSize: 10,
                      color: "red",
                      background: "rgba(255,255,255,0.6)",
                      padding: "0 2px",
                      borderRadius: 2,
                    }}
                  >
                    {key}
                  </span>
                </div>
              ))}
            </div>
          )}
          {/* Menú contextual */}
          {ctxMenu.open && (
            <div
              className="absolute z-40"
              style={{ left: ctxMenu.x, top: ctxMenu.y }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-white shadow-lg border rounded-md w-44 overflow-hidden">
                <button
                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                  onClick={chooseDiagnostico}
                >
                  Agregar diagnóstico…
                </button>
                <button
                  className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                  onClick={deleteDraft}
                >
                  Eliminar este punto
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* UI flotante */}
      <div className={`
          sm:hidden fixed inset-y-0 left-0 z-40
          w-56 max-w-[80vw]
          bg-white/95 backdrop-blur shadow-lg
          transform transition-transform duration-200
          ${toolsOpen ? "translate-x-0" : "-translate-x-full"}
          flex flex-col gap-3 p-3
        `}>
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Herramientas</span>
          <button
            className="px-2 py-1 text-sm rounded bg-gray-100"
            onClick={() => setToolsOpen(false)}
          >
            Cerrar
          </button>
        </div>
        <div className="h-px bg-gray-200" />
        <div className="flex flex-col gap-4 overflow-y-auto">
        {/* Tamaño */}
        <div className="flex flex-col items-center">
          <span className="text-xs mb-2">Tamaño</span>
          <div className="h-40 flex items-center justify-center">
            <input
              type="range"
              min="6"
              max="40"
              value={sizePx}
              onChange={(e) => setSizePx(parseInt(e.target.value))}
              className="origin-center rotate-[-90deg]"
              style={{ width: "160px" }}
              aria-label="Tamaño del punto (móvil)"
            />
          </div>
          <span className="text-[11px]">{sizePx}px</span>
        </div>
        <div className="h-px bg-gray-200" />
        <div className="max-h-64 overflow-y-auto pr-1" style={{ scrollbarWidth: "thin" }}>
          <div className="grid grid-cols-4 gap-3">
            {colors.map((c) => (
              <button
                key={c}
                onClick={() => setSelectedColor(c)}
                title={c}
                className="mx-auto"
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  background: c,
                  border: selectedColor === c ? "2px solid #000" : "1px solid #bbb",
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
    {toolsOpen && (
        <button
          className="sm:hidden fixed inset-0 z-30 bg-black/20"
          onClick={() => setToolsOpen(false)}
          aria-label="Cerrar herramientas"
        />
      )}
    </div>
  );
}
